import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import api from "../../../Component/axios_Interceptor/api";

import url, { SERVER_ADDRESS } from "../../../Component/server";

import { fontStyles } from "../../../Component/style/admin_font";

import { Icon, RowView2 } from "../../../Component/style/common_style";
import Layout_Admin from "../../../Component/UI/Layout_Admin";
import AdminPageHeader from "../../../Component/UI/AdminPageHeader";

import { StoreInfo } from "../../../Component/Interface/Store";
import BankAccounts from "./BankAccounts";
import LiveDetailDialog from "../LiveList/Component/LiveDetailDialog";
import StorePolicyContent from "../../../Component/UI/StorePolicyContent";
import { customerPolicyDraft } from "./customer-policy-draft";


// 스토어 정보 타입 확장 (인터페이스 외 신규 필드 대응)

interface ExtendedStoreInfo extends StoreInfo {
  banner_img?: string;
  banner_link_url?: string;
  store_intro?: string;
  customer_terms?: string | null;
  customer_privacy?: string | null;
}

const businessFields = [
  { key: "com_name", label: "상호 (법인명)", maxLength: 255 },
  { key: "rep_name", label: "대표자명", maxLength: 255 },
  { key: "business_reg_number", label: "사업자등록번호", maxLength: 12 },
  { key: "mail_order_registration_number", label: "통신판매업 신고번호", maxLength: 100 },
  { key: "tax_email", label: "세금계산서 수신 이메일", maxLength: 254 },
  { key: "business_type", label: "업태", maxLength: 100 },
  { key: "business_item", label: "종목", maxLength: 100 },
  { key: "addr_num", label: "사업장 우편번호", maxLength: 255 },
  { key: "addr_basic", label: "사업장 기본주소", maxLength: 255 },
  { key: "addr_detail", label: "사업장 상세주소", maxLength: 255 },
] as const;
type BusinessKey = typeof businessFields[number]["key"];
type BusinessInfo = Record<BusinessKey, string>;
const readBusinessInfo = (data?: ExtendedStoreInfo): BusinessInfo => Object.fromEntries(
  businessFields.map(({ key }) => [key, data?.[key] ?? ""])
) as BusinessInfo;

const Setting = () => {
  const [storInfo, setStoreInfo] = useState<ExtendedStoreInfo>();
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>(() => readBusinessInfo());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [empty, setEmpty] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState<"save" | "upload" | null>(null);
  const operationRef = useRef<"save" | "upload" | null>(null);
  const requestRef = useRef(0);
  const mounted = useRef(false);


  // 상태값 정의

  const [profileImg, setProfileImg] = useState<string>("");
  const [bannerImg, setBannerImg] = useState<string>("");
  const [bannerLink, setBannerLink] = useState<string>("");
  const [storeIntro, setStoreIntro] = useState<string>("");
  const [storePhone, setStorePhone] = useState<string>("");
  const [storeEmail, setStoreEmail] = useState<string>("");
  const [customerTerms, setCustomerTerms] = useState<string>("");
  const [customerPrivacy, setCustomerPrivacy] = useState<string>("");
  const [sellerName, setSellerName] = useState("");
  const [sellerNameChecked, setSellerNameChecked] = useState(true);
  const [checkingSellerName, setCheckingSellerName] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const closeCompanyModal = useCallback(() => setCompanyModalOpen(false), []);
  const [policyModal, setPolicyModal] = useState<"terms" | "privacy" | null>(null);
  const [policyPreview, setPolicyPreview] = useState(false);
  const openPolicy = (type: "terms" | "privacy") => {setPolicyPreview(false); setPolicyModal(type);};
  const loadPolicyDraft = () => {
    if (!policyModal || !storInfo) return;
    const current = policyModal === "privacy" ? customerPrivacy : customerTerms;
    if (current.trim() && !window.confirm("작성 중인 내용을 기본 초안으로 바꿀까요? 설정 저장 전까지 공개 내용은 변경되지 않습니다.")) return;
    const draft = customerPolicyDraft(policyModal, {...storInfo, ...businessInfo, store_name: sellerName, store_phone: storePhone, store_email: storeEmail});
    if (policyModal === "privacy") setCustomerPrivacy(draft); else setCustomerTerms(draft);
    setPolicyPreview(false);
  };


  const profileInputRef = useRef<HTMLInputElement | null>(null);

  const bannerInputRef = useRef<HTMLInputElement | null>(null);



  // 데이터 로드

  const get_info = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setLoadError("");
    setEmpty(false);
    try {
      const res = await api.get(url.Communication_url.store_info_summary);
      if (!mounted.current || request !== requestRef.current) return false;
      const data: ExtendedStoreInfo = res.data;
      if (!data) {
        setStoreInfo(undefined);
        setEmpty(true);
        return false;
      }
      if (!Number.isInteger(data.seq) || data.seq <= 0) throw new Error("Invalid store response");
      setStoreInfo(data);
      setBusinessInfo(readBusinessInfo(data));
      setProfileImg(data.profile_img || "");
      setBannerImg(data.banner_img || "");
      setBannerLink(data.banner_link_url || "");
      setStoreIntro(data.store_intro || "");
      setStorePhone(data.store_phone || "");
      setStoreEmail(data.store_email || "");
      setCustomerTerms(data.customer_terms || "");
      setCustomerPrivacy(data.customer_privacy || "");
      setSellerName(data.store_name || "");
      setSellerNameChecked(true);
      setMessage("");
      return true;
    } catch {
      if (mounted.current && request === requestRef.current) {
        setLoadError("스토어 정보를 불러오지 못했습니다. 다시 시도해주세요.");
      }
      return false;
    } finally {
      if (mounted.current && request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    get_info();
    return () => { mounted.current = false; requestRef.current += 1; };
  }, [get_info]);


  const imageTypes = new Set(["image/png", "image/jpeg"]);
  const maxImageSize = 10 * 1024 * 1024;

  const errorMessage = (cause: any, fallback: string) => {
    const detail = cause?.response?.data?.message;
    return Array.isArray(detail) ? detail.join(" ") : typeof detail === "string" ? detail : fallback;
  };



  const handleProfileClick = () => {

    profileInputRef.current?.click();

  };



  const handleBannerClick = () => {

    bannerInputRef.current?.click();

  };



  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: "profile" | "banner") => {

    const file = e.target.files?.[0];
    if (!file || operationRef.current || loading || loadError) return;


    if (!imageTypes.has(file.type)) {
      setError("png, jpg, jpeg 파일만 첨부 가능합니다.");
      e.target.value = "";
      return;
    }
    if (file.size > maxImageSize) {
      setError("이미지는 10MB 이하로 첨부해주세요.");
      e.target.value = "";
      return;
    }

    operationRef.current = "upload";
    setBusy("upload");
    setError("");
    setMessage("");
    try {
      const uploadUrl = type === "profile"
        ? url.Communication_url.upload_store_profile
        : url.Communication_url.upload_store_banner;
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(uploadUrl, form);

      if (!mounted.current) return;
      if (res.data?.message?.includes("성공") && typeof res.data.file_path === "string") {
        const filePath = res.data.file_path;
        const field = type === "profile" ? "profile_img" : "banner_img";
        const saved = await api.patch(url.Communication_url.store_update, {
          store_seq: storInfo?.seq,
          [field]: filePath,
        });
        if (saved.data !== "성공") throw new Error("Save failed");

        let confirmedPath = filePath;
        try {
          const refreshed = await api.get(url.Communication_url.store_info_summary);
          const refreshedPath = refreshed.data?.[field];
          // A read routed to another instance can briefly return the previous image.
          // Keep the path acknowledged by the successful upload/update instead of
          // replacing the preview with that stale value.
          if (refreshedPath === filePath) confirmedPath = refreshedPath;
        } catch {
          if (mounted.current) setMessage("이미지는 저장됐지만 최신 정보 확인에 실패했습니다. 화면에는 저장된 이미지를 표시합니다.");
        }
        if (!mounted.current) return;
        setStoreInfo(previous => previous ? { ...previous, [field]: confirmedPath } : previous);
        if (type === "profile") setProfileImg(confirmedPath);
        else setBannerImg(confirmedPath);
        setMessage(previous => previous || `${type === "profile" ? "로고" : "배너"} 이미지가 저장되었습니다.`);
      } else throw new Error("Upload failed");
    } catch (cause: any) {
      if (mounted.current) setError(errorMessage(cause, "이미지를 저장하지 못했습니다. 다시 시도해주세요."));
    } finally {
      operationRef.current = null;
      if (mounted.current) setBusy(null);
      if (e.target) e.target.value = "";

    }

  };



  const deleteImage = async (e: React.MouseEvent, type: "profile" | "banner") => {
    e.stopPropagation();
    if (!storInfo || operationRef.current || loading || loadError) return;
    operationRef.current = "save";
    setBusy("save");
    setError("");
    setMessage("");
    const field = type === "profile" ? "profile_img" : "banner_img";
    try {
      const saved = await api.patch(url.Communication_url.store_update, { store_seq: storInfo.seq, [field]: "" });
      if (saved.data !== "성공") throw new Error("Save failed");
      if (!mounted.current) return;
      setStoreInfo(previous => previous ? { ...previous, [field]: "" } : previous);
      if (type === "profile") setProfileImg("");
      else setBannerImg("");
      try {
        const refreshed = await api.get(url.Communication_url.store_info_summary);
        const confirmed = typeof refreshed.data?.[field] === "string" ? refreshed.data[field] : "";
        setStoreInfo(previous => previous ? { ...previous, [field]: confirmed } : previous);
        if (type === "profile") setProfileImg(confirmed);
        else setBannerImg(confirmed);
        setMessage(`${type === "profile" ? "로고" : "배너"} 이미지가 삭제되었습니다.`);
      } catch {
        setMessage("이미지는 삭제됐지만 최신 정보 확인에 실패했습니다.");
      }
    } catch (cause: any) {
      if (mounted.current) setError(errorMessage(cause, "이미지를 삭제하지 못했습니다. 다시 시도해주세요."));
    } finally {
      operationRef.current = null;
      if (mounted.current) setBusy(null);
    }
  };



  // 스토어 정보 통합 저장

  const checkSellerName = async () => {
    const nextName = sellerName.trim();
    if (!storInfo || nextName === (storInfo.store_name || "")) { setSellerNameChecked(true); return; }
    if (!/^[a-zA-Z0-9가-힣]{2,10}$/.test(nextName)) {
      setError("셀러명은 영문, 숫자, 한글로 2~10자 입력해주세요."); return;
    }
    setCheckingSellerName(true); setError(""); setMessage("");
    try {
      await api.get(url.Communication_url.seller_name_availability, { params: { store_name: nextName } });
      setSellerNameChecked(true); setMessage("사용 가능한 셀러명입니다.");
    } catch (cause: any) { setSellerNameChecked(false); setError(errorMessage(cause, "셀러명 중복 검사를 완료하지 못했습니다.")); }
    finally { setCheckingSellerName(false); }
  };

  const handleSaveSettings = async (event?: React.FormEvent, closeCompanyDialog = false) => {
    event?.preventDefault();
    if (!storInfo || operationRef.current || loading || loadError) return;
    setError("");
    setMessage("");
    // Only send edited business fields, preserving original values during unrelated changes.
    const original = readBusinessInfo(storInfo);
    const businessChanges: Partial<BusinessInfo> = {};
    for (const { key } of businessFields) {
      if (businessInfo[key] !== original[key]) businessChanges[key] = businessInfo[key].trim();
    }
    if (businessChanges.business_reg_number !== undefined) {
      const number = businessChanges.business_reg_number.replace(/[-\s]/g, "");
      if (number && !/^\d{10}$/.test(number)) {
        setError("사업자등록번호는 숫자 10자리로 입력해주세요.");
        return;
      }
      businessChanges.business_reg_number = number;
    }
    if (businessChanges.tax_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(businessChanges.tax_email)) {
      setError("세금계산서 수신 이메일을 올바르게 입력해주세요.");
      return;
    }
    if (storeEmail !== (storInfo.store_email || "") && storeEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(storeEmail.trim())) {
      setError("셀러 페이지에 표시할 이메일을 올바르게 입력해주세요.");
      return;
    }
    if (sellerName.trim() !== (storInfo.store_name || "") && !sellerNameChecked) {
      setError("셀러명 중복 검사를 완료해주세요.");
      return;
    }

    const displayValues = {
      profile_img: profileImg,
      banner_img: bannerImg,
      banner_link_url: bannerLink,
      store_intro: storeIntro,
      store_phone: storePhone.trim(),
      store_email: storeEmail.trim(),
      customer_terms: customerTerms.trim(),
      customer_privacy: customerPrivacy.trim(),
    };
    const displayChanges = Object.fromEntries(Object.entries(displayValues).filter(
      ([key, value]) => value !== (storInfo[key as keyof typeof displayValues] ?? "")
    ));
    if (sellerName.trim() !== (storInfo.store_name || "")) displayChanges.store_name = sellerName.trim();
    const sendData = {
      store_seq: storInfo.seq,
      ...displayChanges,
      ...businessChanges,
    };
    if (Object.keys(sendData).length === 1) {
      setMessage("변경된 내용이 없습니다.");
      if (closeCompanyDialog) setCompanyModalOpen(false);
      return;
    }

    operationRef.current = "save";
    setBusy("save");
    try {
      const res = await api.patch(url.Communication_url.store_update, sendData);
      if (!mounted.current) return;
      if (res.data !== "성공") throw new Error("Save failed");
      const refreshed = await get_info();
      if (mounted.current) {
        if (closeCompanyDialog) setCompanyModalOpen(false);
        setMessage(refreshed ? "스토어 설정이 저장되었습니다." : "저장은 완료됐지만 최신 정보를 불러오지 못했습니다. 다시 불러오기를 눌러주세요.");
      }
    } catch (cause: any) {
      if (mounted.current) {
        const detail = cause?.response?.data?.message;
        setError(Array.isArray(detail) ? detail.join(" ") : typeof detail === "string" ? detail : "스토어 정보를 저장하지 못했습니다. 입력 내용은 유지됩니다. 다시 시도해주세요.");
      }
    } finally {
      operationRef.current = null;
      if (mounted.current) setBusy(null);
    }

  };



  return (

    <Layout_Admin menuname="설정">

      <Container>

        <AdminPageHeader title="스토어 설정" description="스토어 정보와 노출 이미지를 관리합니다." />

        {loading && <StatusText role="status">스토어 정보를 불러오는 중입니다.</StatusText>}
        {loadError && <StatusText role="alert">{loadError} <RetryBtn type="button" disabled={!!busy} onClick={() => get_info()}>다시 불러오기</RetryBtn></StatusText>}
        {empty && <StatusText role="status">등록된 스토어 정보가 없습니다.</StatusText>}
        {storInfo && <form aria-label="스토어 설정" onSubmit={handleSaveSettings} noValidate>
          <SettingsFields disabled={loading || !!loadError || !!busy}>
            <SettingsLayout>
              <PreviewColumn>
                <PreviewHeading>
                  <strong>셀러 페이지 이미지 설정</strong>
                </PreviewHeading>
                <ProfilePreview aria-label="셀러 프로필 미리보기">
                  <PreviewBanner type="button" aria-label="배너 사진 등록" onClick={handleBannerClick}>
                    {bannerImg ? (
                      <img src={bannerImg.startsWith("http") ? bannerImg : `${SERVER_ADDRESS}openfile/${bannerImg}`} alt="banner-preview" />
                    ) : (
                      <span>배너 이미지를 등록해주세요.</span>
                    )}
                    <EditOverlay><Icon aria-hidden="true" src={require("../../../Image/Admin/ic_setting_camera.png")} /> 배너 변경</EditOverlay>
                  </PreviewBanner>
                  <PreviewProfileRow>
                    <PreviewAvatar type="button" aria-label="프로필 사진 등록" onClick={handleProfileClick}>
                      {profileImg ? (
                        <img src={profileImg.startsWith("http") ? profileImg : `${SERVER_ADDRESS}openfile/${profileImg}`} alt="profile-preview" />
                      ) : (
                        <span>{(storInfo.store_name || "S").slice(0, 1)}</span>
                      )}
                      <AvatarEdit aria-hidden="true">변경</AvatarEdit>
                    </PreviewAvatar>
                    <div>
                      <PreviewStoreName>{sellerName || "스토어명"}</PreviewStoreName>
                    </div>
                  </PreviewProfileRow>
                  <PreviewContentEditor>
                    <PreviewContentHeading>
                      <label htmlFor="store-intro">내용</label>
                      <span>셀러 페이지에 표시할 소개글입니다.</span>
                    </PreviewContentHeading>
                    <IntroTextArea
                      id="store-intro"
                      aria-label="스토어 소개글"
                      placeholder="셀러 페이지에 보여줄 소개 내용을 입력해주세요."
                      value={storeIntro}
                      onChange={(event) => setStoreIntro(event.target.value)}
                    />
                    <HelperText>{storeIntro.length.toLocaleString()}자</HelperText>
                  </PreviewContentEditor>
                  {bannerLink && <PreviewLink>{bannerLink}</PreviewLink>}
                  <PreviewFooter>
                    <PreviewFooterLinks><span>이용약관</span><span>개인정보처리방침</span></PreviewFooterLinks>
                    {businessInfo.com_name && <span>상호 : {businessInfo.com_name}</span>}
                    {businessInfo.rep_name && <span>대표자 : {businessInfo.rep_name}</span>}
                    {businessInfo.business_reg_number && <span>사업자등록번호 : {businessInfo.business_reg_number}</span>}
                    {businessInfo.mail_order_registration_number && <span>통신판매업 신고번호 : {businessInfo.mail_order_registration_number}</span>}
                    {businessInfo.business_type && <span>업태 : {businessInfo.business_type}</span>}
                    {businessInfo.business_item && <span>종목 : {businessInfo.business_item}</span>}
                    {(businessInfo.addr_num || businessInfo.addr_basic || businessInfo.addr_detail) && <span>사업장 주소 : {[businessInfo.addr_num && `(${businessInfo.addr_num})`, businessInfo.addr_basic, businessInfo.addr_detail].filter(Boolean).join(" ")}</span>}
                    {storePhone && <span>연락처 : {storePhone}</span>}
                    {storeEmail && <span>E-mail : {storeEmail}</span>}
                  </PreviewFooter>
                </ProfilePreview>
              </PreviewColumn>

              <EditorPanel>
                <EditorHeading>
                  <div>
                    <strong>페이지 세부 정보</strong>
                    <span>배너 연결 주소와 셀러 페이지 하단 정보를 관리합니다.</span>
                  </div>
                  <CompanyButton type="button" onClick={() => setCompanyModalOpen(true)}>회사 정보 관리</CompanyButton>
                </EditorHeading>

                <Field htmlFor="seller-name">
                  <span>셀러명</span>
                  <SellerNameRow>
                    <BusinessInput id="seller-name" aria-label="셀러명" value={sellerName} maxLength={10}
                      onChange={event => { setSellerName(event.target.value); setSellerNameChecked(event.target.value.trim() === (storInfo.store_name || "")); }} />
                    <CompanyButton type="button" disabled={checkingSellerName || sellerName.trim() === (storInfo.store_name || "")}
                      onClick={() => void checkSellerName()}>{checkingSellerName ? "확인 중..." : sellerNameChecked && sellerName.trim() !== (storInfo.store_name || "") ? "확인 완료" : "중복 검사"}</CompanyButton>
                  </SellerNameRow>
                </Field>

                <Field htmlFor="banner-link">
                  <span>배너 연결 주소</span>
                  <LinkInput
                    id="banner-link"
                    aria-label="배너 링크"
                    type="text"
                    placeholder="배너를 눌렀을 때 이동할 주소"
                    value={bannerLink}
                    onChange={(e) => setBannerLink(e.target.value)}
                  />
                </Field>

                <ContactSection>
                  <SectionTitle>셀러 페이지 하단 정보</SectionTitle>
                  <SectionDescription>고객이 실제 셀러 페이지 하단에서 확인할 연락처와 이메일입니다.</SectionDescription>
                  <ContactGrid>
                    <Field htmlFor="store-phone">
                      <span>대표 연락처</span>
                      <BusinessInput id="store-phone" aria-label="대표 연락처" value={storePhone}
                        onChange={event => setStorePhone(event.target.value)} placeholder="등록된 연락처 없음" />
                    </Field>
                    <Field htmlFor="store-email">
                      <span>대표 이메일</span>
                      <BusinessInput id="store-email" type="email" aria-label="대표 이메일" value={storeEmail}
                        onChange={event => setStoreEmail(event.target.value)} placeholder="등록된 이메일 없음" />
                    </Field>
                  </ContactGrid>
                </ContactSection>

                <BankAccounts disabled={!!busy || loading || !!loadError} />

                <ContactSection>
                  <SectionTitle>고객 약관</SectionTitle>
                  <SectionDescription>스토어 공개 페이지에 표시할 이용약관과 개인정보처리방침입니다. 비워두면 플랫폼 기본 정책이 표시됩니다.</SectionDescription>
                  <PolicyButtons>
                    <PolicyButton type="button" aria-haspopup="dialog" onClick={() => openPolicy("terms")}>
                      <span><strong>이용약관</strong><small>{customerTerms.trim() ? `${customerTerms.length.toLocaleString()}자 입력됨` : "플랫폼 기본 약관 사용"}</small></span>
                      작성/수정
                    </PolicyButton>
                    <PolicyButton type="button" aria-haspopup="dialog" onClick={() => openPolicy("privacy")}>
                      <span><strong>개인정보처리방침</strong><small>{customerPrivacy.trim() ? `${customerPrivacy.length.toLocaleString()}자 입력됨` : "플랫폼 기본 방침 사용"}</small></span>
                      작성/수정
                    </PolicyButton>
                  </PolicyButtons>
                </ContactSection>

                <ImageActions>
                  <button type="button" onClick={handleBannerClick}>배너 이미지 변경</button>
                  <button type="button" onClick={handleProfileClick}>로고 이미지 변경</button>
                  {bannerImg && <button className="danger" type="button" onClick={event => deleteImage(event, "banner")}>배너 삭제</button>}
                  {profileImg && <button className="danger" type="button" onClick={event => deleteImage(event, "profile")}>로고 삭제</button>}
                </ImageActions>

                {message && <StatusText role="status">{message}</StatusText>}
                {!companyModalOpen && error && <ErrorText role="alert">{error}</ErrorText>}
                <SubmitRow>
                  <SubmitBtn type="submit">{busy === "save" ? "저장 중..." : busy === "upload" ? "이미지 업로드 중..." : "설정 저장"}</SubmitBtn>
                </SubmitRow>
              </EditorPanel>
            </SettingsLayout>

            <input type="file" ref={profileInputRef} onChange={(e) => handleFileChange(e, "profile")}
              accept="image/png,image/jpeg" style={{ display: "none" }} />
            <input type="file" ref={bannerInputRef} onChange={(e) => handleFileChange(e, "banner")}
              accept="image/png,image/jpeg" style={{ display: "none" }} />

            <LiveDetailDialog footer={<ModalActions>
                <button type="button" onClick={() => handleSaveSettings(undefined, true)} disabled={!!busy}>
                  {busy === "save" ? "저장 중..." : "회사 정보 저장"}
                </button>
              </ModalActions>}
              open={companyModalOpen}
              onClose={closeCompanyModal}
              title="회사·사업자 정보"
              description="회사 정보와 세금계산서 정보를 입력한 뒤 아래 회사 정보 저장 버튼을 눌러주세요."
              size="large"
            >
              <BusinessGrid>
                {businessFields.map(({ key, label, maxLength }) => (
                  <Field key={key} htmlFor={`store-${key}`}>
                    <span>{label}</span>
                    <BusinessInput
                      id={`store-${key}`}
                      type={key === "tax_email" ? "email" : "text"}
                      inputMode={key === "business_reg_number" ? "numeric" : key === "tax_email" ? "email" : "text"}
                      maxLength={maxLength}
                      value={businessInfo[key]}
                      placeholder={key === "business_reg_number" ? "숫자 10자리 (하이픈 입력 가능)" : undefined}
                      onChange={(event) => setBusinessInfo((previous) => ({ ...previous, [key]: event.target.value }))}
                    />
                  </Field>
                ))}
              </BusinessGrid>
              {error && <ErrorText role="alert">{error}</ErrorText>}
              
            </LiveDetailDialog>
            <LiveDetailDialog footer={<ModalActions>
                <button type="button" onClick={() => setPolicyModal(null)}>편집 완료</button>
              </ModalActions>}
              open={policyModal !== null}
              onClose={() => setPolicyModal(null)}
              title={policyModal === "privacy" ? "개인정보처리방침 작성" : "이용약관 작성"}
              description="입력한 내용은 아래의 설정 저장 버튼을 눌러야 실제 셀러 페이지에 반영됩니다. 비워두면 플랫폼 기본 정책을 사용합니다."
              size="large"
            >
              <HelperText>기본 초안을 불러온 뒤 판매 조건과 대괄호 항목(택배사·개인정보 보호책임자 등)을 실제 정보로 수정해 주세요. 설정 저장 전까지 구매자 화면은 변경되지 않습니다.</HelperText>
              <ModalActions style={{gap: "var(--space-2)", flexWrap: "wrap", marginBottom: "var(--space-4)"}}>
                <button type="button" onClick={loadPolicyDraft}>기본 초안 불러오기</button>
                <button type="button" onClick={() => setPolicyPreview(value => !value)} aria-pressed={policyPreview}>{policyPreview ? "본문 편집" : "미리보기"}</button>
              </ModalActions>
              {policyPreview ? <PolicyPreview aria-label="고객 약관 미리보기"><StorePolicyContent policy={{key: policyModal === "privacy" ? "seller_privacy" : "seller_terms", title: "", content: (policyModal === "privacy" ? customerPrivacy : customerTerms) || "아직 작성한 내용이 없습니다. 기본 초안을 불러와 작성할 수 있습니다.", effectiveDate: "", version: null, publishedAt: null}} showMetadata={false} /></PolicyPreview> :
              <Field htmlFor="customer-policy-content">
                <span>{policyModal === "privacy" ? "개인정보처리방침" : "이용약관"}</span>
                <PolicyTextArea
                  id="customer-policy-content"
                  aria-label={policyModal === "privacy" ? "고객 개인정보처리방침" : "고객 이용약관"}
                  value={policyModal === "privacy" ? customerPrivacy : customerTerms}
                  onChange={event => policyModal === "privacy" ? setCustomerPrivacy(event.target.value) : setCustomerTerms(event.target.value)}
                  maxLength={200000}
                  placeholder={policyModal === "privacy" ? "플랫폼 기본 개인정보처리방침 사용" : "플랫폼 기본 이용약관 사용"}
                />
                <HelperText>{(policyModal === "privacy" ? customerPrivacy : customerTerms).length.toLocaleString()} / 200,000자</HelperText>
              </Field>}
              
            </LiveDetailDialog>
          </SettingsFields>
        </form>}
      </Container>

    </Layout_Admin>

  );

};



export default Setting;

const PolicyPreview = styled.div`
  min-width: 0;
  padding: var(--space-4);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  background: var(--color-bg-surface);
  font-size: var(--text-body);
`;



const Container = styled.div`
  box-sizing: border-box;

  background-color: var(--admin-canvas);
  min-height: 40rem;

  display: flex;

  flex-direction: column;

`;



const SectionTitle = styled.h2`

  font-size: 15px;

  color: var(--admin-ink);

  ${fontStyles.Bold}

  margin: 1.5rem 0 1rem 0;

`;



const LinkInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  min-height: 44px;
  border: 1px solid var(--admin-theme-border, #e0e0e0);

  border-radius: 6px;

  padding: 0 1rem;

  font-size: 14px;

  color: var(--admin-ink);

  outline: none;

  background-color: var(--admin-theme-surface, white);

  margin-bottom: 0;
  ${fontStyles.regular}

  &::placeholder {

    color: var(--admin-muted);

  }

  &:focus {
    border-color: var(--admin-theme-link, var(--admin-accent));
    box-shadow: 0 0 0 3px #e6defb;
  }
`;



const PolicyTextArea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  min-height: 180px;
  border: 1px solid var(--admin-theme-border, #e0e0e0);
  border-radius: 6px;
  padding: 12px;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
  background: var(--admin-theme-surface, white);
  color: var(--admin-ink);
  &:focus { border-color: var(--admin-theme-link, var(--admin-accent)); outline: none; box-shadow: 0 0 0 3px #e6defb; }
`;

const PolicyButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const PolicyButton = styled.button`
  min-height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid var(--admin-theme-border, #ddd8e4);
  border-radius: 10px;
  background: var(--admin-theme-surface, #ffffff);
  color: var(--admin-theme-link, #5730c4);
  ${fontStyles.semiBold}
  text-align: right;
  cursor: pointer;
  > span { min-width: 0; text-align: left; }
  strong { display: block; color: var(--admin-theme-text, #302a38); font-size: 14px; }
  small { display: block; margin-top: 5px; color: var(--admin-theme-muted, #77717f); font-size: 12px; font-family: var(--font-family-base); font-weight: var(--font-weight-regular); }
  &:hover { border-color: #9277df; background: var(--admin-theme-raised, #faf8ff); }
  &:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
`;


const SubmitRow = styled(RowView2)`

  width: 100%;

  justify-content: flex-end;
  margin-top: 24px;
`;



const SubmitBtn = styled.button`
  border: 0;
  min-width: 11rem;
  min-height: 48px;
  border-radius: 10px;
  color: white;

  font-size: 14px;

  ${fontStyles.Bold}

  background: var(--admin-solid, #292433);

  cursor: pointer;

  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
  &:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
  &:disabled { opacity: .55; cursor: wait; }
`;

const SettingsLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 500px) minmax(0, 1fr);
  gap: 32px;
  align-items: flex-start;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewColumn = styled.section`
  width: 100%;
  max-width: 500px;
  min-width: 0;
  position: sticky;
  top: 16px;
  justify-self: center;
  @media (max-width: 1100px) { position: static; }
`;

const PreviewHeading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;

  strong { display: block; color: var(--admin-theme-text, #29242f); font-size: 18px; ${fontStyles.Bold} }
  div > span { display: block; margin-top: 5px; color: var(--admin-theme-muted, #6e6875); font-size: 13px; }

  @media (max-width: 420px) {
    flex-direction: column;
    gap: 10px;
  }
`;


const EditorPanel = styled.section`
  min-width: 0;
  padding: 22px;
  border: 1px solid var(--admin-theme-border, #e5e1e9);
  border-radius: 16px;
  background: var(--admin-theme-surface, #ffffff);
  box-shadow: 0 8px 28px rgba(41, 31, 68, 0.06);
`;

const EditorHeading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 18px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--admin-theme-border, #ebe8ef);

  strong { display: block; color: var(--admin-theme-text, #29242f); font-size: 18px; ${fontStyles.Bold} }
  div > span { display: block; margin-top: 5px; color: var(--admin-theme-muted, #6e6875); font-size: 13px; line-height: 1.45; }

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const CompanyButton = styled.button`
  flex: 0 0 auto;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid var(--admin-theme-border, #d5cbea);
  border-radius: 9px;
  background: var(--admin-theme-raised, #faf8ff);
  color: var(--admin-theme-link, #5730c4);
  ${fontStyles.semiBold}
  cursor: pointer;
  &:hover { background: var(--admin-theme-accent-soft, #f0ebff); }
  &:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
  @media (max-width: 640px) { width: 100%; }
`;

const SellerNameRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  button { min-height: 44px; }
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const HelperText = styled.small`
  color: var(--admin-theme-muted, #b8b4bd);
  font-size: 12px;
  line-height: 1.5;
`;

const ContactSection = styled.section`
  margin-top: 24px;
  padding-top: 4px;
  border-top: 1px solid var(--admin-theme-border, #ebe8ef);
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const ImageActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;

  button {
    min-height: 40px;
    padding: 0 12px;
    border: 1px solid var(--admin-theme-border, #ddd8e4);
    border-radius: 8px;
    background: var(--admin-theme-surface, #ffffff);
    color: var(--admin-theme-text, #4d4755);
    cursor: pointer;
  }
  button:hover { border-color: #8464dd; color: var(--admin-theme-link, #5730c4); background: var(--admin-theme-raised, #faf8ff); }
  button.danger { color: var(--admin-theme-danger, #a23b3b); border-color: var(--admin-theme-border, #ead2d2); }
`;

const ProfilePreview = styled.aside`
  width: 100%;
  max-width: 500px;
  min-width: 0;
  border: 1px solid #464149;
  border-radius: 16px;
  overflow: hidden;
  background: var(--admin-solid, #292433);
  color: #ffffff;
  box-shadow: 0 16px 42px rgba(41, 31, 68, 0.1);
`;

const PreviewBanner = styled.button`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  padding: 0;
  border: 0;
  background: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d6d1df;
  font-size: 13px;
  cursor: pointer;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &:focus-visible { outline: 4px solid #8b6cf2; outline-offset: -4px; }
  &:hover > div { opacity: 1; }
`;

const EditOverlay = styled.div`
  position: absolute;
  right: 16px;
  bottom: 16px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0 12px;
  border-radius: 9px;
  background: rgba(22, 18, 29, 0.78);
  color: #ffffff;
  ${fontStyles.semiBold}
  font-size: 13px;
  opacity: .88;
  transition: opacity .15s ease;
  img { width: 18px; height: 18px; filter: brightness(0) invert(1); }
`;

const PreviewProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 16px 12px;
`;

const PreviewAvatar = styled.button`
  position: relative;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, var(--admin-accent), #4b20c2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-family-base); font-weight: var(--font-weight-bold);
  cursor: pointer;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &:focus-visible { outline: 3px solid #8b6cf2; outline-offset: 3px; }
`;

const AvatarEdit = styled.span`
  position: absolute;
  inset: auto 0 0;
  padding: 3px 0;
  background: rgba(20, 17, 25, .72);
  color: #ffffff;
  font-size: 10px;
`;

const PreviewStoreName = styled.div`
  color: #ffffff;
  font-size: 18px;
  ${fontStyles.Bold}
`;

const PreviewContentEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 16px 20px;
`;

const PreviewContentHeading = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  label { color: #ffffff; font-size: 14px; ${fontStyles.semiBold} }
  span { color: var(--admin-theme-muted, #aaa6af); font-size: 11px; text-align: right; }

  @media (max-width: 420px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
    span { text-align: left; }
  }
`;

const IntroTextArea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  min-height: 136px;
  border: 1px solid #5a565f;
  border-radius: 8px;
  padding: 14px;
  font-size: 14px;
  line-height: 1.6;
  color: #ffffff;
  background: #383838;
  resize: vertical;
  outline: none;
  margin-bottom: 0;
  ${fontStyles.regular}
  &::placeholder { color: var(--admin-theme-muted, #a9a6ac); }
  &:focus {
    border-color: #a78cff;
    box-shadow: 0 0 0 3px rgba(167, 140, 255, .24);
  }
`;



const PreviewLink = styled.div`
  margin: 0 1rem 1rem;
  padding: 0.65rem 0.75rem;
  border-radius: 6px;
  background: #383838;
  color: var(--admin-theme-muted, #d4d0d8);
  font-size: 12px;
  overflow-wrap: anywhere;
`;

const PreviewFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 16px;
  padding: 24px 16px 32px;
  border-top: 1px solid var(--color-border-default);
  background: var(--color-bg-surface);
  color: var(--color-text-secondary);
  font-size: 13px;
  line-height: 1.7;

  span { overflow-wrap: anywhere; }
`;

const PreviewFooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 10px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--color-border-default);
  color: var(--color-text-primary);
  ${fontStyles.semiBold}
`;

const SettingsFields = styled.fieldset`
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
  &:disabled { opacity: 0.65; }
  button:disabled, &:disabled button { cursor: default; }
`;
const SectionDescription = styled.p`
  max-width: 40rem;
  margin: -0.25rem 0 1.25rem;
  color: var(--admin-theme-muted, #61616b);
  font-size: 13px;
  line-height: 1.6;
`;
const BusinessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: var(--admin-ink);
  font-size: 13px;
  min-width: 0;
`;
const BusinessInput = styled(LinkInput)`
  margin: 0;
  min-height: 44px;
  padding: 0 0.75rem;
`;
const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  button {
    min-width: 112px;
    min-height: 44px;
    padding: 0 18px;
    border: 0;
    border-radius: 9px;
    background: #4f28be;
    color: #ffffff;
    ${fontStyles.semiBold}
    cursor: pointer;
  }
  button:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
`;
const StatusText = styled.div`
  margin: 1rem 0;
  color: var(--admin-theme-text, #42424d);
  font-size: 14px;
  line-height: 1.6;
`;
const ErrorText = styled(StatusText)`color: var(--admin-theme-danger, #b42318);`;
const RetryBtn = styled.button`
  margin-left: 0.5rem;
  border: 1px solid var(--admin-theme-border, #ccc);
  border-radius: 6px;
  padding: 0.4rem 0.7rem;
  background: var(--admin-theme-surface, white);
  cursor: pointer;
`;
